import { useQuery } from "@tanstack/react-query";

const API_KEY = import.meta.env.VITE_HERE_API_KEY as string | undefined;

// HERE Routing API v8 returns spans against a route polyline. There is no
// point-query endpoint — the workaround is a trivial 2-point route where
// destination is origin offset by a small delta, then read the first span.
// ~0.0001° ≈ 11 m at German latitudes, enough for HERE not to reject "too close".
const DELTA = 0.0001;

interface HereSpan {
  speedLimit?: number; // m/s; field ABSENT on unrestricted segments (autobahn)
}

interface HereRoutesResponse {
  routes?: Array<{
    sections?: Array<{
      spans?: HereSpan[];
    }>;
  }>;
}

export type HereSpeedLimitState =
  | { state: "no-key" }
  | { state: "no-coords" }
  | { state: "idle" }
  | { state: "loading" }
  | { state: "unrestricted" }
  | { state: "no-data" }
  | { state: "error"; message: string }
  | { state: "ok"; kph: number };

// 5 decimals ≈ 1.1 m — two reports on the same road segment share a cache entry.
function roundKey(n: number): string {
  return n.toFixed(5);
}

async function fetchSpeedLimit(
  lat: number,
  lng: number,
  signal: AbortSignal,
): Promise<HereRoutesResponse> {
  const url = new URL("https://router.hereapi.com/v8/routes");
  url.searchParams.set("transportMode", "car");
  url.searchParams.set("origin", `${lat},${lng}`);
  url.searchParams.set("destination", `${lat + DELTA},${lng + DELTA}`);
  url.searchParams.set("return", "polyline");
  url.searchParams.set("spans", "speedLimit");
  url.searchParams.set("apikey", API_KEY ?? "");

  const res = await fetch(url.toString(), { signal });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`HERE Routing ${res.status}: ${body.slice(0, 200)}`);
  }
  return (await res.json()) as HereRoutesResponse;
}

export function useHereSpeedLimit(
  lat: number | undefined,
  lng: number | undefined,
  requested: boolean,
): HereSpeedLimitState {
  const hasKey = typeof API_KEY === "string" && API_KEY.length > 0;
  const hasCoords = typeof lat === "number" && typeof lng === "number";
  const enabled = hasKey && hasCoords && requested;

  const query = useQuery({
    queryKey: ["hereSpeedLimit", hasCoords ? roundKey(lat) : null, hasCoords ? roundKey(lng) : null],
    queryFn: ({ signal }) => fetchSpeedLimit(lat as number, lng as number, signal),
    enabled,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24,
    retry: false,
  });

  if (!hasKey) return { state: "no-key" };
  if (!hasCoords) return { state: "no-coords" };
  if (query.data) {
    const firstSpan = query.data.routes?.[0]?.sections?.[0]?.spans?.[0];
    if (!firstSpan) return { state: "no-data" };
    // Field absent on an otherwise-present span = no posted limit (unrestricted autobahn).
    if (typeof firstSpan.speedLimit !== "number") return { state: "unrestricted" };
    return { state: "ok", kph: Math.round(firstSpan.speedLimit * 3.6) };
  }
  if (!requested) return { state: "idle" };
  if (query.isLoading || query.isFetching) return { state: "loading" };
  if (query.isError) {
    return { state: "error", message: query.error instanceof Error ? query.error.message : "Unknown error" };
  }
  return { state: "no-data" };
}
